import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query"
import {handleError} from "helpers"
import {calendarService} from "services"
import {LinkedCalendarInfo, UserSettings} from "types/calendar"

export const CALENDAR_USER_SETTINGS_QUERY_KEY = ["calendar-user-settings"]

type UseMyCalendarSettingsOptions = {
    enabled?: boolean
}

export const getValidDefaultCalendarId = (
    settings: UserSettings | undefined,
    calendarOptions: Pick<LinkedCalendarInfo, "calendarId">[]
): number | null => {
    const savedDefaultCalendarId = settings?.defaultCalendarId ? Number(settings.defaultCalendarId) : null
    const hasSavedDefault =
        !!savedDefaultCalendarId && calendarOptions.some((calendar) => calendar.calendarId === savedDefaultCalendarId)

    return hasSavedDefault ? savedDefaultCalendarId : null
}

export const useMyCalendarSettings = (options?: UseMyCalendarSettingsOptions) => {
    const queryClient = useQueryClient()
    const enabled = options?.enabled ?? true

    const settingsQ = useQuery<UserSettings>({
        queryKey: CALENDAR_USER_SETTINGS_QUERY_KEY,
        queryFn: () => calendarService.getUserSettings(),
        enabled,
        onError: (error) => handleError(error)
    })

    const updateM = useMutation({
        mutationFn: (data: Partial<{defaultCalendarId: number; selectedCalendarIds: number[]}>) =>
            calendarService.updateUserSettings(data),
        onSuccess: () => {
            queryClient.invalidateQueries(CALENDAR_USER_SETTINGS_QUERY_KEY)
        },
        onError: (error) => handleError(error)
    })

    return {
        settings: settingsQ.data,
        data: settingsQ.data,
        isLoading: settingsQ.isLoading,
        isFetching: settingsQ.isFetching,
        update: updateM.mutateAsync,
        isUpdating: updateM.isLoading
    }
}
